import { simplifyContinuityLine } from "@/lib/behavior/viewModels/buildCoachSessionPrepContinuity";
import type { CanonicalMomentEvent, ContinuityRecordSummary } from "@/lib/behavior/continuityRecord";
import {
  buildCoachingRecordSummary,
  buildLeadershipRecordSummary,
} from "@/lib/behavior/continuityRecord";
import type { LeaderContinuityThread } from "@/lib/behavior/leaderContinuityThread";

export interface ContinuityRuntime {
  headline: string;
  summary: string;
  changed: string[];
  carryForward: string[];
  next: string[];
  examples: {
    changed: string[];
    carryForward: string[];
    next: string[];
  };
  narrative: string;
  empty: boolean;
  thread?: LeaderContinuityThread | null;
  recordSummary?: ContinuityRecordSummary | null;
}

function normalizeLine(
  value: string | null | undefined,
  options?: { simplify?: boolean },
): string | null {
  const normalized = value
    ? options?.simplify
      ? simplifyContinuityLine(value)
      : value.replace(/\s+/g, " ").trim()
    : null;
  return normalized ? normalized : null;
}

function isAcceptableSimplifiedLine(value: string): boolean {
  const normalized = value.trim();
  if (!normalized) return false;
  if (normalized.length < 12) return false;
  const normalizedForLeadCheck = normalized.replace(/^[\s•\-*.,;:()[\]{}'"`]+/, "");
  if (/^[a-z]/.test(normalizedForLeadCheck)) return false;
  if (!/[.!?]/.test(normalized) && normalized.split(/\s+/).length < 4) return false;
  return true;
}

function uniqueLines(
  values: Array<string | null | undefined>,
  limit = 4,
  options?: { simplify?: boolean },
): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const value of values) {
    const normalized = normalizeLine(value, options);
    if (normalized && options?.simplify && !isAcceptableSimplifiedLine(normalized)) continue;
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    result.push(normalized);
    if (result.length >= limit) break;
  }
  return result;
}

function firstLine(lines: string[]): string | null {
  return lines[0] || null;
}

function buildNarrative(params: {
  changed: string[];
  carryForward: string[];
  next: string[];
}): string {
  const changed = firstLine(params.changed);
  const carryForward = firstLine(params.carryForward);
  const next = firstLine(params.next);

  if (changed && carryForward && next) {
    return `What changed: ${changed} Carry this forward: ${carryForward} Next: ${next}`;
  }
  if (changed && carryForward) {
    return `What changed: ${changed} Carry this forward: ${carryForward}`;
  }
  if (carryForward && next) {
    return `Carry this forward: ${carryForward} Next: ${next}`;
  }
  return changed || carryForward || next || "Continuity will strengthen as more shared evidence accumulates.";
}

export function buildContinuityRuntime(params: {
  headline: string;
  summary: string;
  changed?: Array<string | null | undefined>;
  carryForward?: Array<string | null | undefined>;
  next?: Array<string | null | undefined>;
  examples?: {
    changed?: Array<string | null | undefined>;
    carryForward?: Array<string | null | undefined>;
    next?: Array<string | null | undefined>;
  };
  simplifyLines?: boolean;
  thread?: LeaderContinuityThread | null;
  recordSummary?: ContinuityRecordSummary | null;
}): ContinuityRuntime {
  const options = { simplify: params.simplifyLines ?? false };
  const changed = uniqueLines(params.changed || [], 4, options);
  const carryForward = uniqueLines(params.carryForward || [], 4, options);
  const next = uniqueLines(params.next || [], 4, options);
  const examples = {
    changed: uniqueLines(params.examples?.changed || [], 2),
    carryForward: uniqueLines(params.examples?.carryForward || [], 2),
    next: uniqueLines(params.examples?.next || [], 2),
  };
  const empty = changed.length === 0 && carryForward.length === 0 && next.length === 0;

  return {
    headline: params.headline,
    summary: params.summary,
    changed,
    carryForward,
    next,
    examples,
    narrative: buildNarrative({ changed, carryForward, next }),
    empty,
    ...(params.thread ? { thread: params.thread } : {}),
    ...(params.recordSummary ? { recordSummary: params.recordSummary } : {}),
  };
}

export function buildLeaderTodayContinuityRuntime(params: {
  topBehaviorLabel: string;
  patternRecognitionLines: string[];
  whatsWorkingLines: string[];
  quickAction: {
    action: string;
    whyNow: string;
  };
  thread?: LeaderContinuityThread | null;
  leadershipMomentEvents?: CanonicalMomentEvent[];
  recordSummary?: ContinuityRecordSummary | null;
}): ContinuityRuntime {
  const activeFocus = params.topBehaviorLabel !== "No active focus yet" ? params.topBehaviorLabel : null;
  return buildContinuityRuntime({
    headline: "Continuity This Week",
    summary: "Your recent evidence, momentum, and next practice move in one place.",
    changed: [
      activeFocus ? `Your current focus is ${activeFocus}.` : null,
      ...params.patternRecognitionLines,
    ],
    carryForward: params.whatsWorkingLines,
    next: [
      params.quickAction.action,
      params.quickAction.whyNow,
    ],
    thread: params.thread,
    recordSummary: params.recordSummary ?? (
      params.leadershipMomentEvents
        ? buildLeadershipRecordSummary({ events: params.leadershipMomentEvents })
        : null
    ),
  });
}

export function buildLeaderThroughlineContinuityRuntime(params: {
  headline: string;
  highlights: string[];
  continuityCues: string[];
  agreementLines: string[];
  evidenceExamples?: {
    changed?: string[];
    carryForward?: string[];
    next?: string[];
  };
  thread?: LeaderContinuityThread | null;
  recordSummary?: ContinuityRecordSummary | null;
}): ContinuityRuntime {
  return buildContinuityRuntime({
    headline: "Shared Continuity",
    summary: params.headline,
    changed: params.highlights,
    carryForward: [
      ...params.agreementLines,
      ...params.continuityCues,
    ],
    next: params.continuityCues,
    examples: {
      changed: params.evidenceExamples?.changed || params.highlights,
      carryForward: params.evidenceExamples?.carryForward || params.agreementLines,
      next: params.evidenceExamples?.next || [],
    },
    simplifyLines: true,
    thread: params.thread,
    recordSummary: params.recordSummary,
  });
}

export function buildCoachPortfolioContinuityRuntime(params: {
  latestMomentLabel: string;
  staleLeadersLabel: string;
  proactiveBriefs: Array<{ title: string; message: string }>;
  macroTopActions: Array<{ reason: string; ctaLabel: string }>;
  coachingMomentEvents?: CanonicalMomentEvent[];
  recordSummary?: ContinuityRecordSummary | null;
}): ContinuityRuntime {
  return buildContinuityRuntime({
    headline: "Portfolio Continuity",
    summary: "The portfolio view of what shifted, what needs to stay in motion, and what to act on next.",
    changed: [
      params.latestMomentLabel,
      ...params.macroTopActions.map((action) => action.reason),
    ],
    carryForward: [
      params.staleLeadersLabel,
      ...params.proactiveBriefs.map((brief) => brief.title),
    ],
    next: [
      ...params.macroTopActions.map((action) => action.ctaLabel),
      ...params.proactiveBriefs.map((brief) => brief.message),
    ],
    recordSummary: params.recordSummary ?? (
      params.coachingMomentEvents
        ? buildCoachingRecordSummary({ events: params.coachingMomentEvents })
        : null
    ),
  });
}

export function buildCoachSessionContinuityRuntime(params: {
  namedAgreements: string[];
  latestCoachGrowthRecord: {
    workedOn: string[];
    direction: string[];
    nextConversationSignals: string[];
  } | null;
  leaderName?: string | null;
  coachingMomentEvents?: CanonicalMomentEvent[];
  recordSummary?: ContinuityRecordSummary | null;
}): ContinuityRuntime {
  return buildContinuityRuntime({
    headline: "Session Continuity",
    summary: "Carry the real agreements, coaching shift, and next-session probe into the next conversation.",
    changed: params.latestCoachGrowthRecord?.workedOn || [],
    carryForward: [
      ...params.namedAgreements,
      ...(params.latestCoachGrowthRecord?.direction || []),
    ],
    next: params.latestCoachGrowthRecord?.nextConversationSignals || [],
    recordSummary: params.recordSummary ?? (
      params.coachingMomentEvents
        ? buildCoachingRecordSummary({
            events: params.coachingMomentEvents,
            leaderName: params.leaderName,
          })
        : null
    ),
  });
}
